import React, { useEffect, useState } from "react"
import { createContainer } from "unstated-next"
import * as voicifyApi from '../api';
import AnalyticsRequestFilter from "../models/analytics/AnalyticsRequestFilter";
import SessionDateModel from "../models/analytics/api/SessionDateModel";
import IResult, { ResultType } from '../models/result/IResult';

export interface BaseEvent {
    sessionId: string;
    eventDate: string;
    requestId: string;
    applicationId: string;
}

export interface SessionListModel {
    sessionId: string,
    startDate: Date,
    phoneNumbers?: string[],
    duration?: number,
    from?: string,
}

function useSessionAnalyticsContainer() {
    const [events, setEvents] = useState({} as { [sessionId: string]: (BaseEvent | any)[] });
    const [sessions, setSessions] = useState([] as SessionListModel[])
    const [sessionsForReview, setSessionsForReview] = useState([] as SessionListModel[])
    const [loading, setLoading] = useState(false);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [error, setError] = useState("")
    const [simpleView, setSimpleView] = useState(false);
    const [allAppOrderPlaces, setAllAppOrderPlaces] = useState([] as any[]);
    const [allAppTransfers, setAllAppTransfers] = useState([] as any[]);

    useEffect(() => {
        // get simpleview boolean from application storage
        const simpleViewStr = localStorage.getItem("VOICIFY_SIMPLE_SESSION_ANALYTICS");
        if (simpleViewStr?.length) {
            // parse the string to boolean
            setSimpleView(simpleViewStr === "true" ? true : false);
        }
    }, []);

    const updateSimpleView = (simple: boolean) => {
        setSimpleView(simple);
        localStorage.setItem("VOICIFY_SIMPLE_SESSION_ANALYTICS", simple ? "true" : "false");
    };

    useEffect(() => {
        setLoadingEvents(false);
    }, [events]);

    const loadSessions = async (appId: string, filter: AnalyticsRequestFilter) => {
        try {
            setLoading(true);
            const tasks = [];
            const sessionFilters = breakUpFilters(filter);
            tasks.push(loadSessionsMultiFilter(appId, sessionFilters));
            tasks.push(voicifyApi.getCallStartedEventsByApplication(appId, filter));
            var results = await Promise.all(tasks);
            let sessionModels = [];
            let callStartedEvents = [];
            if (results[0].resultType === "Ok") {
                sessionModels = results[0].data as SessionDateModel[];
            } else {
                setError(results[0].errors.join(", "));
            }
            if (results[1].resultType === "Ok") {
                if (results[1].data?.length) {
                    callStartedEvents = results[1].data as any[];
                }
            } else {
                setError(results[1].errors.join(", "));
            }

            const newSessions = [] as SessionListModel[];
            for (const session of sessionModels ?? []) {
                const callStarted = callStartedEvents.find(e => e.sessionId === session.sessionId);
                const phoneNumbers = [];
                if (callStarted?.from?.length)
                    phoneNumbers.push(callStarted.from);
                if (callStarted?.to?.length)
                    phoneNumbers.push(callStarted.to);
                if (callStarted?.phoneNumber?.length)
                    phoneNumbers.push(callStarted.phoneNumber);
                newSessions.push({
                    sessionId: session.sessionId,
                    startDate: new Date(session.startDate),
                    phoneNumbers: phoneNumbers,
                    from: callStarted?.from
                });

            }
            // sort sessions by most recent start date first
            newSessions.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
            setSessions(newSessions);
            setLoading(false);
        } catch (e) {
            console.log(e);
            setError(e.message);
        }
    };

    const loadSessionsMultiFilter = async (appId: string, filters: AnalyticsRequestFilter[]): Promise<IResult<SessionDateModel[]>> => {
        try {
            const tasks = [];
            for (const filter of filters) {
                tasks.push(voicifyApi.getSessionsByApplication(appId, filter));
            }
            var results = await Promise.all(tasks);
            let sessionModels = [];
            for (const result of results) {
                if (result.resultType === "Ok") {
                    sessionModels = sessionModels.concat(result.data as SessionDateModel[]);
                } else {
                    setError(result.errors.join(", "));
                }
            }
            return {
                resultType: "Ok",
                data: sessionModels,
                errors: []
            };
        } catch (e) {
            console.log(e);
            setError(e.message);
            null;
        }
    }

    const breakUpFilters = (filter: AnalyticsRequestFilter): AnalyticsRequestFilter[] => {
        let sessionFilters = [filter];
        if (filter.startDate && filter.endDate) {
            const diffDays = Math.ceil((new Date(filter.endDate).getTime() - new Date(filter.startDate).getTime()) / (1000 * 3600 * 24));
            if (diffDays > 7) {
                let done = false;
                sessionFilters = [];
                let startDate = new Date(filter.startDate);
                while (!done) {
                    const endDate = new Date(startDate.getTime() + (7 * 24 * 60 * 60 * 1000));
                    if (endDate > new Date(filter.endDate)) {
                        done = true;
                        sessionFilters.push({
                            startDate: startDate.toISOString(),
                            endDate: filter.endDate
                        });
                    } else {
                        sessionFilters.push({
                            startDate: startDate.toISOString(),
                            endDate: endDate.toISOString()
                        });
                        startDate = new Date(endDate.toISOString());
                    }
                }
            }
        }
        return sessionFilters;
    }
    const loadSessionsForReview = async (appId: string, minDuration: number = 20, daysBack: number = 2) => {
        try {
            setLoading(true);
            const tasks = [];
            const filter: AnalyticsRequestFilter = {
                startDate: new Date(new Date().setDate(new Date().getDate() - daysBack)).toISOString(),
                endDate: new Date(new Date().setDate(new Date().getDate() + 2)).toISOString(),
            }
            // if filter. startDate and endDate are bigger than 30 days, load by week. 
            const sessionFilters = breakUpFilters(filter);
            tasks.push(loadSessionsMultiFilter(appId, sessionFilters));
            tasks.push(voicifyApi.getCallCompleteEventsByApplication(appId, filter));
            tasks.push(voicifyApi.getOrderPlacedEventsByApplication(appId, filter));
            tasks.push(voicifyApi.getCallTransferredEventsByApplication(appId, filter));
            var results = await Promise.all(tasks);
            let sessionModels = [];
            let callCompleteEvents = [];
            if (results[0].resultType !== "Ok") {
                return "Invalid";
            }
            if (results[0].resultType === "Ok") {
                sessionModels = results[0].data as SessionDateModel[];
            } else {
                setError(results[0].errors.join(", "));
            }
            if (results[1].resultType === "Ok") {
                if (results[1].data?.length) {
                    callCompleteEvents = results[1].data as any[];
                }
            } else {
                setError(results[1].errors.join(", "));
            }

            if (results[2].resultType === "Ok") {
                if (results[2]?.data?.length) {
                    setAllAppOrderPlaces(results[2]?.data as any[]);
                }
            }

            if (results[3].resultType === "Ok") {
                if (results[3]?.data?.length) {
                    setAllAppTransfers(results[3]?.data as any[]);
                }
            }
            const newSessions = [] as SessionListModel[];
            for (const session of sessionModels ?? []) {
                const callComplete = callCompleteEvents.find(e => e.sessionId === session.sessionId);
                if (callComplete?.duration && callComplete?.duration > minDuration) {
                    const phoneNumbers = [];
                    if (callComplete?.from?.length)
                        phoneNumbers.push(callComplete.from);
                    if (callComplete?.to?.length)
                        phoneNumbers.push(callComplete.to);
                    if (callComplete?.phoneNumber?.length)
                        phoneNumbers.push(callComplete.phoneNumber);
                    newSessions.push({
                        sessionId: session.sessionId,
                        startDate: new Date(session.startDate),
                        phoneNumbers: phoneNumbers,
                        duration: callComplete.duration,
                        from: callComplete?.from
                    });
                }
            }
            // sort sessions by most recent start date first
            newSessions.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
            setSessionsForReview(newSessions);
            setLoading(false);
            return "Ok";
        } catch (e) {
            console.log(e);
            setError(e.message);
            return "Invalid";
        }
    };

    const loadLatestRecentContentHitsForSession = async (appId: string, sessionId: string) => {
        try {
            const filter: AnalyticsRequestFilter = {
                startDate: new Date(new Date().setDate(new Date().getDate() - 10)).toISOString(),
                endDate: new Date(new Date().setDate(new Date().getDate() + 5)).toISOString(),
            }
            const contentHitsResponse = await voicifyApi.getContentHits(appId, filter, 0, 50, true, sessionId, "");
            if (contentHitsResponse.resultType === "Ok") {
                return contentHitsResponse.data;
            }
            return [];
        }
        catch {
            return [];
        }
    }

    const loadRecentOrderPlacesForSession = (sessionId: string) => {
        try {
            const orderPlaces = allAppOrderPlaces.filter(o => o.sessionId === sessionId);
            return [...orderPlaces];
        }
        catch {
            return [];
        }
    }
    const loadRecentTransfersForSession = (sessionId: string) => {
        try {
            const events = allAppTransfers.filter(o => o.sessionId === sessionId);
            return [...events];
        }
        catch {
            return [];
        }
    }

    const loadEvents = async (appId: string, sessionId: string) => {
        setLoadingEvents(true);
        const eventsResponse = await voicifyApi.getAllEventsForSession(appId, sessionId);
        if (eventsResponse.resultType === "Ok") {
            eventsResponse.data.forEach(e => {
                if (e.eventDate) {
                    e.timestamp = new Date(e.eventDate).getTime()
                }
            })
            setEvents({ ...events, [sessionId]: eventsResponse.data });
            return eventsResponse.data;
        } else {
            setError(eventsResponse.errors.join(", "));
        }
    };

    const loadEventsForSession = async (appId: string, sessionId: string, forceEventLoad?: boolean): Promise<any[]> => {
        try {
            setError("");
            if (forceEventLoad || !(sessionId in events)) {
                const loadEventsResponse = await loadEvents(appId, sessionId);
                return loadEventsResponse;
            } else {
                return events[sessionId];
            }
        } catch (e) {
            setError(e.message);
        }
    };

    const getRecordingUrls = async (appId: string, sessionId: string, processed: boolean): Promise<string> => {
        try {
            const recordingResponse = await voicifyApi.getCallRecording(appId, sessionId, processed);
            if (recordingResponse.resultType === "Ok") {
                return recordingResponse.data.url;
            } else {
                setError(recordingResponse.errors.join(", "));
            }

        } catch (e) {
            setError(e.message);
        }
    };


    const loadSessionsForOrgs = async (orgIds: string[], filter: AnalyticsRequestFilter, logCallback?: (message: string) => void): Promise<SessionListModel[]> => {
        try {
            setLoading(true);
            setError("");

            const allSessionListModels: SessionListModel[] = [];
            const loadErrors: string[] = [];
            const pageSize = 200;

            // Process each organization one at a time
            for (let i = 0; i < orgIds.length; i++) {
                const orgId = orgIds[i];
                try {
                    logCallback?.(`Processing organization ${i + 1} of ${orgIds.length}...`);

                    // Helper function to load all sessions for this org with pagination
                    const loadAllSessionsForOrg = async (): Promise<SessionDateModel[]> => {
                        let allSessions: SessionDateModel[] = [];
                        let skip = 0;
                        let hasMoreData = true;
                        let orgSessionCount = 0;

                        while (hasMoreData) {
                            const result = await voicifyApi.getSessionsByOrganizationId(orgId, filter, skip, pageSize);

                            if (result.resultType === "Ok") {
                                allSessions = [...allSessions, ...result.data];
                                orgSessionCount += result.data.length;

                                if (result.data.length < pageSize) {
                                    hasMoreData = false;
                                } else {
                                    skip += pageSize;
                                }
                            } else {
                                const errorMsg = `Failed to load sessions for org ${orgId}: ${result?.errors?.[0] || 'Unknown error'}`;
                                loadErrors.push(errorMsg);
                                logCallback?.(errorMsg);
                                hasMoreData = false;
                            }
                        }

                        logCallback?.(`Loaded ${orgSessionCount} sessions for organization ${i + 1}`);
                        return allSessions;
                    };

                    // Helper function to load all call complete events for this org with pagination
                    const loadAllCallCompleteEventsForOrg = async (): Promise<any[]> => {
                        let allCallCompleteEvents: any[] = [];
                        let skip = 0;
                        let hasMoreData = true;
                        let orgCallCompleteCount = 0;

                        while (hasMoreData) {
                            const paginatedFilter = {
                                ...filter,
                                skip,
                                take: pageSize
                            };
                            const result = await voicifyApi.getOrgCallCompleteEvents(orgId, paginatedFilter);

                            if (result.resultType === "Ok") {
                                allCallCompleteEvents = [...allCallCompleteEvents, ...result.data];
                                orgCallCompleteCount += result.data.length;

                                if (result.data.length < pageSize) {
                                    hasMoreData = false;
                                } else {
                                    skip += pageSize;
                                }
                            } else {
                                const errorMsg = `Failed to load call complete events for org ${orgId}: ${result?.errors?.[0] || 'Unknown error'}`;
                                loadErrors.push(errorMsg);
                                logCallback?.(errorMsg);
                                hasMoreData = false;
                            }
                        }

                        logCallback?.(`Loaded ${orgCallCompleteCount} call complete events for organization ${i + 1}`);
                        return allCallCompleteEvents;
                    };

                    // Load sessions and call complete events in parallel for this org
                    const [orgSessions, orgCallCompleteEvents] = await Promise.all([
                        loadAllSessionsForOrg(),
                        loadAllCallCompleteEventsForOrg()
                    ]);

                    // Process sessions for this org immediately
                    for (const session of orgSessions) {
                        const callComplete = orgCallCompleteEvents.find(e => e.sessionId === session.sessionId);
                        const phoneNumbers = [];
                        let duration = undefined;
                        let from = undefined;

                        if (callComplete) {
                            duration = callComplete.duration;
                            from = callComplete.from;

                            if (callComplete.from?.length)
                                phoneNumbers.push(callComplete.from);
                            if (callComplete.to?.length)
                                phoneNumbers.push(callComplete.to);
                            if (callComplete.phoneNumber?.length)
                                phoneNumbers.push(callComplete.phoneNumber);
                        }

                        allSessionListModels.push({
                            sessionId: session.sessionId,
                            startDate: new Date(session.startDate),
                            phoneNumbers: phoneNumbers,
                            duration: duration,
                            from: from
                        });
                    }

                } catch (error) {
                    const errorMsg = `Failed to process org ${orgId}: ${error.message || 'Unknown error'}`;
                    loadErrors.push(errorMsg);
                    logCallback?.(errorMsg);
                }
            }

            if (loadErrors.length > 0) {
                setError(loadErrors.join('; '));
            }

            // Sort all sessions by most recent start date first
            allSessionListModels.sort((a, b) => b.startDate.getTime() - a.startDate.getTime());

            setSessions(allSessionListModels);
            return allSessionListModels;
        } catch (e) {
            console.log(e);
            setError(e.message);
            return [];
        } finally {
            setLoading(false);
        }
    };

    return {
        loadSessions,
        loadSessionsForReview,
        loadEventsForSession,
        loadLatestRecentContentHitsForSession,
        loadRecentOrderPlacesForSession,
        loadRecentTransfersForSession,
        loadSessionsForOrgs,
        sessions,
        sessionsForReview,
        events,
        loading,
        loadingEvents,
        allAppTransfers,
        simpleView,
        updateSimpleView,
        getRecordingUrl: getRecordingUrls,
        error
    };

}

const SessionAnalyticsContainer = createContainer(useSessionAnalyticsContainer);
export default SessionAnalyticsContainer;
